import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@rentflowdev/ui'
import { useAccountCurrency } from '@/lib/use-account-currency'
import { CURRENCY_SIGNS } from '@/types'

interface CurrencyFieldProps {
	/** Omit to render the value with no label, e.g. inside a table row. */
	label?: string
	/** A currency already frozen onto the record being edited. */
	value?: string
	className?: string
	/** Hidden when the field sits in a row that repeats, to avoid N links. */
	showChangeLink?: boolean
}

/**
 * The currency, shown but not chosen.
 *
 * It used to be a picker in eight separate forms, each with its own copy of the
 * RUB/USD/EUR list and nothing keeping their answers in step — a property could
 * carry rent in one currency and its utilities in another. It is now settled
 * once in the profile.
 *
 * Shown rather than hidden on purpose: the figure beside it is a sum of money
 * in a contract, and a bare number is ambiguous. Given `value`, that stored
 * currency wins over the profile — an existing record is denominated in what
 * was agreed, not in today's setting.
 */
export function CurrencyField({
	label, value, className, showChangeLink = false,
}: CurrencyFieldProps) {
	const { t } = useTranslation()
	const accountCurrency = useAccountCurrency()
	const currency = value ?? accountCurrency

	return (
		<div className={`flex flex-col gap-2 ${className ?? ''}`}>
			{label && (
				<span className="flex items-center justify-between gap-2 text-sm font-[500] text-(--color-foreground)">
					{label}
					{showChangeLink && (
						<Button
							variant="link"
							size="sm"
							className="h-auto p-0 text-xs font-normal"
							render={<Link to="/settings/profile" />}
						>
							{t('settings.profile.currency.change')}
						</Button>
					)}
				</span>
			)}
			<Input
				disabled
				value={`${t(`currencies.${currency}`)} (${CURRENCY_SIGNS[currency] ?? currency})`}
			/>
		</div>
	)
}
